import { WalletConfig } from '@/features/swap/logic/types'
import { GSMMintError, GsmMintState } from './types'
import { DialogFormNormalizedData } from '@/features/dialogs/common/logic/form'
import { getChainConfigEntry } from '@/config/chain'
import { assert } from '@/utils/assert'
import { erc20Abi } from 'viem'
import { toBigInt } from '@/utils/bigNumber'
import { hyperEVM } from '@/config/chain/constants'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { gsmConfig } from '@/config/contracts-generated'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'
import { captureError } from '../../../../instrumentation-client'
import { queryClient } from '@/config/query-client'
import { allowanceQueryKey } from '@/features/actions/flavours/approve/logic/query'
import { getBalancesQueryKeyPrefix } from '@/domain/wallet/getBalancesQueryKeyPrefix'

interface GsmMintProps {
  setGsmMintState: (state: GsmMintState) => void
  fromAsset: DialogFormNormalizedData
  walletConfig: WalletConfig
  chainId: number
  onSuccess: () => void
}

export async function gsmMint({ setGsmMintState, fromAsset, walletConfig, chainId, onSuccess }: GsmMintProps) {
  const { account, publicClient, walletClient } = walletConfig

  if (!account || !publicClient || !walletClient) {
    return
  }

  try {
    setGsmMintState({ message: 'Initializing...', status: 'in-progress' })

    const { markets } = getChainConfigEntry(chainId)
    const { nativeAssetInfo } = markets ?? {}
    assert(nativeAssetInfo, 'Native asset info is required for mint')

    const { nativeAssetSymbol } = nativeAssetInfo

    const gsmAddress = getContractAddress(gsmConfig.address, chainId)
    const gsmAbi = gsmConfig.abi

    const inputAmount = toBigInt(fromAsset.token.toBaseUnit(fromAsset.value))

    if (fromAsset.token.symbol !== nativeAssetSymbol) {
      const allowance = await publicClient.readContract({
        address: fromAsset.token.address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [account, gsmAddress],
      })

      if (allowance < toBigInt(inputAmount)) {
        setGsmMintState({ message: 'Approving...', status: 'in-progress' })

        const approvePermit = await walletClient.writeContract({
          address: fromAsset.token.address,
          abi: erc20Abi,
          functionName: 'approve',
          args: [gsmAddress, inputAmount],
          chain: hyperEVM,
          account,
        })
        await waitForTxReceipt(publicClient, approvePermit)
      }
    }

    setGsmMintState({ message: 'Minting...', status: 'in-progress' })

    const { request } = await publicClient.simulateContract({
      address: gsmAddress,
      abi: gsmAbi,
      // @TODO: will need to be changed when we support different directions
      functionName: 'sellAsset',
      args: [inputAmount, account],
      chain: hyperEVM,
      account,
    })

    const hash = await walletClient.writeContract(request)

    await waitForTxReceipt(publicClient, hash)

    void queryClient.invalidateQueries({
      queryKey: allowanceQueryKey({ token: fromAsset.token.address, spender: gsmAddress, account, chainId }),
    })

    void queryClient.invalidateQueries({
      queryKey: getBalancesQueryKeyPrefix({ chainId, account }),
    })

    setGsmMintState({ message: '', status: 'idle' })
    onSuccess()
  } catch (error) {
    setGsmMintState({ message: 'An error occurred while minting', status: 'error' })
    captureError(new GSMMintError('GSM mint error'), { error })
  }
}
